// src/pages/Marketing/MarketingI18nContext.js
//
// A small, DEDICATED i18n context for the marketing site — deliberately not
// a reuse of the tenant app's own I18nContext (that one drags in tenant-only
// API calls/permission machinery that doesn't exist for an anonymous
// visitor on posflect.com — see this feature's own plan doc's Grounding
// section and MarketingApp.js's own doc comment on why the whole Marketing
// tree stays isolated from both TenantApp and PlatformApp). Fetches the two
// public marketing endpoints directly (getPublicMarketingLanguages,
// getPublicMarketingContent) and re-fetches content whenever the visitor
// switches language, flipping document.documentElement.dir to match.
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getPublicMarketingContent, getPublicMarketingLanguages } from "../../services/api";

const MarketingI18nContext = createContext(null);

export function MarketingI18nProvider({ children }) {
  const [languages, setLanguages] = useState([]);
  const [languageCode, setLanguageCode] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadContent = useCallback(async (code) => {
    try {
      const data = await getPublicMarketingContent(code);
      setContent(data);
    } catch (err) {
      setError(err.message || "Failed to load marketing content");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const langs = await getPublicMarketingLanguages();
        setLanguages(langs || []);
        const defaultLang = (langs || []).find((l) => l.is_default) || langs?.[0];
        const code = defaultLang?.code || null;
        setLanguageCode(code);
        await loadContent(code);
      } catch (err) {
        setError(err.message || "Failed to load the marketing site");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadContent]);

  useEffect(() => {
    const lang = languages.find((l) => l.code === languageCode);
    document.documentElement.dir = lang?.direction === "rtl" ? "rtl" : "ltr";
    document.documentElement.lang = languageCode || "en";
  }, [languageCode, languages]);

  // SEO meta-sync (this feature's own SEO plan doc) — content.seo.title/
  // description are an admin-editable MarketingContentKey pair exactly
  // like every other section this context already exposes (see
  // seed_marketing_content.go's own "seo" section), just applied to
  // document.title/<meta name="description"> and their Open Graph/Twitter
  // Card counterparts instead of visible page copy. frontend/public/
  // index.html ships static English defaults for the same reason App.js's
  // own TitleSync/MetaDescriptionSync do for the tenant app: this effect
  // never runs for a non-JS-executing crawler or a social-media
  // link-unfurler (WhatsApp/Facebook/Twitter/LinkedIn) — it only benefits a
  // real visitor's browser tab and a JS-executing crawler like Googlebot,
  // which re-reads the DOM after render. og:locale is the one tag here
  // that's actually TRUE per language (this really is what's currently
  // rendered); canonical/og:url deliberately stay OUT of this effect,
  // fixed at the single real URL index.html already sets statically —
  // there is no distinct URL per language on this SPA, so a valid
  // hreflang/canonical-per-language equivalent isn't possible without
  // building server-side rendering first, and claiming one anyway would be
  // an incorrect signal, not a helpful one.
  useEffect(() => {
    const seo = content?.seo;
    if (!seo) return;

    const upsertMeta = (attrName, attrValue, text) => {
      if (!text) return;
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", text);
    };

    if (seo.title) document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:locale", languageCode === "ar" ? "ar_AE" : "en_US");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
  }, [content, languageCode]);

  const switchLanguage = useCallback(async (code) => {
    setLanguageCode(code);
    setLoading(true);
    await loadContent(code);
    setLoading(false);
  }, [loadContent]);

  return (
    <MarketingI18nContext.Provider value={{ languages, languageCode, content, loading, error, switchLanguage }}>
      {children}
    </MarketingI18nContext.Provider>
  );
}

export function useMarketingI18n() {
  const ctx = useContext(MarketingI18nContext);
  if (!ctx) throw new Error("useMarketingI18n must be used within a MarketingI18nProvider");
  return ctx;
}
